import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { openRazorpay } from "@/lib/razorpay";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Coins, Zap, Loader2, ArrowDownCircle, ArrowUpCircle, Gift } from "lucide-react";

export default function Credits() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topupAmount, setTopupAmount] = useState(300);
  const [topupLoading, setTopupLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/credits/history", { params: { limit: 200 } });
      setTxns(data.transactions || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); refresh(); }, []);

  const handleTopup = async () => {
    if (topupAmount < 300) {
      toast.error("Minimum top-up is ₹300 (15 credits).");
      return;
    }
    setTopupLoading(true);
    try {
      await openRazorpay({
        purpose: "topup",
        amount: Number(topupAmount),
        onSuccess: () => {
          refresh();
          load();
        },
      });
    } finally {
      setTopupLoading(false);
    }
  };

  const txnIcon = (type, delta) => {
    if (type === "signup_bonus") return <Gift size={14} className="text-[#FF4500]" />;
    if (delta > 0) return <ArrowUpCircle size={14} className="text-emerald-600" />;
    return <ArrowDownCircle size={14} className="text-red-600" />;
  };

  const typeLabel = {
    signup_bonus: "Welcome bonus",
    topup: "Credit top-up",
    payperuse: "Pay-per-use",
    subscription: "Pro subscription",
    jingle: "Jingle generated",
    refund: "Refund",
  };

  return (
    <div className="min-h-screen bg-white" data-testid="credits-page">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2">Credits</div>
            <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tighter">
              Wallet & history
            </h1>
          </div>
          <div className="bg-black text-white px-6 py-4 text-right">
            <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold">Current balance</div>
            <div className="font-display text-4xl font-semibold tabular-nums mt-1">
              {user?.credits ?? "—"}
              <span className="text-sm font-normal text-white/60 ml-1">credits</span>
            </div>
          </div>
        </div>

        {/* Top-up card (always visible at top) */}
        <div className="bg-[#F8F9FA] border border-black/10 p-6 md:p-8 mb-10" data-testid="credits-topup-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#FF4500] font-bold mb-4">
            <Zap size={14} /> Top up credits
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-zinc-500">Amount (₹)</Label>
              <Input
                type="number"
                min={300}
                step={50}
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)}
                className="mt-2 rounded-sm border-black/20 text-lg font-semibold h-12"
                data-testid="credits-topup-input"
              />
              <div className="flex gap-2 mt-3 flex-wrap">
                {[300, 500, 1000, 2000, 5000].map((a) => (
                  <button
                    key={a}
                    onClick={() => setTopupAmount(a)}
                    className={`flex-1 min-w-[70px] px-3 py-2 border text-xs font-semibold ${
                      Number(topupAmount) === a
                        ? "bg-black text-white border-black"
                        : "bg-white border-black/20 hover:border-black"
                    }`}
                    data-testid={`credits-topup-preset-${a}`}
                  >
                    ₹{a}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-col justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-2">You'll receive</div>
                <div className="font-display text-5xl font-semibold tracking-tighter tabular-nums">
                  +{Math.floor(Number(topupAmount) / 20)}
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  credits · ≈ {Math.floor(Number(topupAmount) / 20 / 15)} × 15-sec jingles · {Math.floor(Number(topupAmount) / 20 / 30)} × 30-sec jingles
                </div>
              </div>
              <Button
                onClick={handleTopup}
                disabled={topupLoading || Number(topupAmount) < 300}
                className="bg-[#FF4500] hover:bg-[#E03C00] text-white rounded-sm h-12 font-semibold mt-6 disabled:opacity-50"
                data-testid="credits-topup-btn"
              >
                {topupLoading ? (
                  <><Loader2 size={16} className="mr-2 animate-spin" /> Opening Razorpay…</>
                ) : (
                  `Pay ₹${topupAmount}`
                )}
              </Button>
            </div>
          </div>
          <div className="mt-4 text-xs text-zinc-500 border-t border-black/10 pt-3">
            Minimum ₹300 · Credits never expire · No subscription required
          </div>
        </div>

        {/* Transaction history */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-2xl font-semibold tracking-tight">Transaction history</h2>
          <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">
            {txns.length} entries
          </div>
        </div>

        {loading ? (
          <div className="py-16 flex items-center justify-center text-zinc-500">
            <Loader2 className="animate-spin mr-2" size={18} /> Loading history…
          </div>
        ) : txns.length === 0 ? (
          <div className="py-16 border border-dashed border-black/20 text-center" data-testid="credits-empty">
            <Coins size={22} className="mx-auto text-zinc-400 mb-3" />
            <div className="font-display text-lg font-semibold mb-1">No transactions yet</div>
            <div className="text-sm text-zinc-500">Generate a jingle or top up to see activity here.</div>
          </div>
        ) : (
          <div className="border border-black/10">
            <div className="grid grid-cols-12 gap-3 px-5 py-3 bg-[#F8F9FA] border-b border-black/10 text-[10px] uppercase tracking-widest font-bold text-zinc-500">
              <div className="col-span-2">Date</div>
              <div className="col-span-3">Type</div>
              <div className="col-span-4">Note</div>
              <div className="col-span-2 text-right">Change</div>
              <div className="col-span-1 text-right">Balance</div>
            </div>
            {txns.map((t) => (
              <div
                key={t.txn_id}
                className="grid grid-cols-12 gap-3 px-5 py-4 border-b border-black/5 text-sm hover:bg-[#F8F9FA]/60 transition-colors"
                data-testid={`txn-row-${t.txn_id}`}
              >
                <div className="col-span-2 text-zinc-500 font-mono text-xs whitespace-nowrap">
                  {new Date(t.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                </div>
                <div className="col-span-3 flex items-center gap-2 font-semibold">
                  {txnIcon(t.type, t.delta)}
                  {typeLabel[t.type] || t.type}
                </div>
                <div className="col-span-4 text-zinc-600 text-xs leading-snug truncate">{t.note}</div>
                <div className={`col-span-2 text-right font-mono font-semibold tabular-nums ${t.delta > 0 ? "text-emerald-600" : "text-red-600"}`}>
                  {t.delta > 0 ? "+" : ""}{t.delta}
                </div>
                <div className="col-span-1 text-right font-mono text-zinc-500 tabular-nums">{t.balance_after}</div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
